import { DeleteOutlined } from '@ant-design/icons';
import type { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';

import { Divider, Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import ToggleRadio from '@/components/Common/ToggleRadio';
import Typo from '@/components/Typography/Typo';
import { cn } from '@/lib/cn';

import type { RowError, ValidateScrapingJobsResponse } from '../api';
import { getFieldProperty } from '../configs/config-actions';
import { SCRAPER_REQ_FIELD_KEY } from '../configs/scraper-field-map/field-const';
import type { SCRAPER_NAME } from '../types';
import type { ScraperInputObj } from '../types/types';

export const CSVPreview = (props: {
  tempData: ScraperInputObj[];
  setTempData: Dispatch<SetStateAction<ScraperInputObj[]>>;
  errors: ValidateScrapingJobsResponse;
  scraperName: SCRAPER_NAME;
}) => {
  const { tempData, setTempData, errors, scraperName } = props;

  return (
    <div className="flex flex-col gap-4">
      <Divider />
      <Typo type="label3" className="text-gray-600">
        Total {tempData.length} requests found
      </Typo>
      <div className="flex flex-col gap-2">
        {tempData.map((row, index) => {
          const rowError = errors.find((error) => error.row_index === index);
          return (
            <RowItem
              key={index}
              row={row}
              error={rowError}
              scraperName={scraperName}
              onRowChange={(row) => {
                setTempData((prev) => {
                  const newArr = [...prev];
                  newArr[index] = row;
                  return newArr;
                });
              }}
              onRowDelete={() => {
                setTempData((prev) => {
                  const newArr = [...prev];
                  newArr.splice(index, 1);
                  return newArr;
                });
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

const RowItem = (props: {
  row: ScraperInputObj;
  error?: RowError;
  onRowChange: (row: ScraperInputObj) => void;
  onRowDelete: () => void;
  scraperName: SCRAPER_NAME;
}) => {
  const { row, error, onRowChange, onRowDelete, scraperName } = props;
  const { title, ...rowRest } = row;
  return (
    <div
      className={cn(
        'p-4 border bg-gray-150 border-gray-300 rounded-md relative',
        error ? 'border-destructive' : 'border-gray-300',
      )}
    >
      <InfoRow>
        <PropertyNameLabel>Title</PropertyNameLabel>
        <Flex f={1} g={8}>
          <Input
            type="text"
            value={title as string}
            onChange={(e) => {
              onRowChange({ ...row, title: e.target.value });
            }}
          />
          <Button
            leftIcon={<DeleteOutlined />}
            onClick={onRowDelete}
            variant="error_ghost"
          >
            Delete
          </Button>
        </Flex>
      </InfoRow>
      {Object.entries(rowRest).map(([key, value]) => {
        const fieldKey = key as SCRAPER_REQ_FIELD_KEY;
        const fieldError = error?.field_errors.find((fieldError) =>
          fieldError.related_fields.includes(fieldKey),
        );
        if (fieldKey === SCRAPER_REQ_FIELD_KEY.KEYWORDS) {
          const keywords = value as string[];
          return (
            <InfoRow
              key={fieldKey}
              className={fieldError ? 'text-destructive' : 'text-gray-700'}
            >
              <PropertyNameLabel>Keywords</PropertyNameLabel>
              {keywords.map((keyword, index) => (
                <div key={keyword} className="flex gap-2 items-center">
                  <Input
                    type="text"
                    value={keyword}
                    onChange={(e) => {
                      onRowChange({
                        ...row,
                        keywords: keywords.map((k, i) =>
                          i === index ? e.target.value : k,
                        ),
                      });
                    }}
                  />
                  <IconButton
                    icon={<DeleteOutlined className="text-destructive" />}
                    outlined={false}
                    onClick={() => {
                      onRowChange({
                        ...row,
                        keywords: keywords.filter((_, i) => i !== index),
                      });
                    }}
                  />
                </div>
              ))}
            </InfoRow>
          );
        }
        const type = typeof value;
        if (type === 'boolean')
          return (
            <InfoRow
              key={fieldKey}
              className={fieldError ? 'text-destructive' : 'text-gray-700'}
            >
              <PropertyNameLabel>
                {getFieldProperty(fieldKey, scraperName)?.name}
              </PropertyNameLabel>
              <ToggleRadio
                status={value as boolean}
                onChange={(status) => {
                  onRowChange({
                    ...row,
                    [fieldKey]: status,
                  });
                }}
              />
            </InfoRow>
          );

        if (type === 'number')
          return (
            <InfoRow
              key={fieldKey}
              className={fieldError ? 'text-destructive' : 'text-gray-700'}
            >
              <PropertyNameLabel>
                {getFieldProperty(fieldKey, scraperName)?.name}
              </PropertyNameLabel>
              <Input
                type="text"
                value={value as string}
                onChange={(e) => {
                  onRowChange({
                    ...row,
                    [fieldKey]: Number(e.target.value),
                  });
                }}
              />
            </InfoRow>
          );
        return (
          <InfoRow
            key={fieldKey}
            className={fieldError ? 'text-destructive' : 'text-gray-700'}
          >
            <PropertyNameLabel>
              {getFieldProperty(fieldKey, scraperName)?.name}
            </PropertyNameLabel>
            <Input
              type="text"
              value={value as string}
              onChange={(e) => {
                onRowChange({
                  ...row,
                  [fieldKey]: e.target.value,
                });
              }}
            />
          </InfoRow>
        );
      })}
    </div>
  );
};

const PropertyNameLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  width: 160px;
`;

const InfoRow = styled(Flex)`
  width: 100%;
  align-items: center;
  gap: 24px;
`;

const Input = styled.input.attrs({
  autoComplete: 'off',
})`
  font-size: 14px;
  height: 28px;
  padding: 2px 8px;
  border-radius: 4px;
  flex: 1;
  &:focus {
    background-color: ${(props) => props.theme.colors.gray100};
    padding-left: 8px;
    outline: 1px solid ${(props) => props.theme.colors.gray700};
  }
  &:active {
    background-color: ${(props) => props.theme.colors.gray100};
    padding-left: 8px;
  }
`;
